import React from "react";
import classNames from "classnames";

import { UseCases } from "@planzone/core";
import {
  useMoveKanbanTask,
  useRetrieveConstraints,
} from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { Draggable, DropBox } from "@common/dragAndDrop";
import { TaskStatus } from "@common/taskStatus";
import { Filter } from "@common/filter";
import { ToggleArrow } from "./toggleArrow";
import { BinIcon } from "./binIcon";
import { TaskCreationWrapper } from "./taskCreationWrapper";
import { Task } from "./task";
import { Props } from "./types";
import styles from "./styles.scss";

function Column({
  column,
  filter,
  project,
  rootList,
  collapse,
  displayAllocationMenu,
  displayTagMenu,
  removeColumn,
}: Props) {
  const { status, tasks } = column;
  const { moveKanbanTask } = useMoveKanbanTask();
  const { pushView } = useModal();
  const { constraints } = useRetrieveConstraints();
  const isCompletedColumn = status.percentComplete === 100;
  const isConstrainedTask = (taskUuid: string) =>
    constraints.some(
      ({ targetEntityUuid, originIsDone }) =>
        targetEntityUuid === taskUuid && !originIsDone,
    );
  const hexToRGBA = (hex: string, opacity: number) => {
    const cleanedHex = hex.replace("#", "");
    const red = parseInt(cleanedHex.substring(0, 2), 16);
    const green = parseInt(cleanedHex.substring(2, 4), 16);
    const blue = parseInt(cleanedHex.substring(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
  };
  const isBasic = status.label.slice(0, "pr_".length) === "pr_";

  return (
    <DropBox
      className={styles.columnDropBox}
      acceptedTypes={["MOVE_TASK"]}
      verticalSlop={50}
      isDisabled={(data) =>
        isCompletedColumn && isConstrainedTask(data.payload?.task?.uuid)
      }
      drop={({ payload }) => {
        payload.column.status.uuid !== column.status.uuid &&
        column.status.percentComplete === 100
          ? pushView({
              key: UseCases.moveKanbanTask,
              data: {
                currentColumn: payload.column,
                nextColumn: column,
                range: {
                  max: null,
                  min: column.tasks[column.tasks.length - 1] || null,
                },
                task: payload.task,
              },
            })
          : moveKanbanTask({
              currentColumn: payload.column,
              nextColumn: column,
              range: {
                max: null,
                min: column.tasks[column.tasks.length - 1] || null,
              },
              task: { ...payload.task, completionDate: null },
            });
      }}
    >
      {({ payload }, canDrop) => (
        <div
          className={classNames(styles.column, {
            [styles.selectedColumn]: canDrop,
          })}
        >
          <div
            className={classNames(styles.heading, {
              [styles.basic]: status.basic,
            })}
          >
            <div className={styles.iconBox} onClick={collapse}>
              <ToggleArrow
                className={styles.icon}
                styles={{ fill: status.color }}
              />
            </div>
            <div
              className={classNames(styles.title, {
                [styles.canDrag]: !isBasic,
              })}
              style={{ color: status.color }}
            >
              <div className={styles.nameAndNumberOfTasks}>
                <TaskStatus status={status} />
                <div
                  className={styles.numberOfTasks}
                  style={{ borderColor: hexToRGBA(status.color, 0.5) }}
                >
                  <p>{tasks.length}</p>
                </div>
              </div>
              {typeof status.percentComplete !== "undefined" &&
                status.percentComplete !== null && (
                  <p className={styles.progress}>{status.percentComplete}%</p>
                )}
              <div
                className={styles.contextualMenu}
                onClick={(e) => {
                  e.stopPropagation();
                  removeColumn(column);
                }}
              >
                <BinIcon className={styles.binIcon} />
              </div>
            </div>
            <div
              className={styles.progressBar}
              style={{
                backgroundColor: status.color,
                width: `${status.percentComplete}%`,
              }}
            />
          </div>
          <div className={styles.tasks}>
            <DropBox
              className={styles.dropBox}
              acceptedTypes={["MOVE_TASK"]}
              verticalSlop={50}
              priority={2}
              isDisabled={(data) =>
                isCompletedColumn && isConstrainedTask(data.payload?.task?.uuid)
              }
              drop={({ payload }) => {
                payload.column.status.uuid !== column.status.uuid &&
                column.status.percentComplete === 100
                  ? pushView({
                      key: UseCases.moveKanbanTask,
                      data: {
                        currentColumn: payload.column,
                        nextColumn: column,
                        range: {
                          min: null,
                          max: column.tasks[0] || null,
                        },
                        task: payload.task,
                      },
                    })
                  : moveKanbanTask({
                      currentColumn: payload.column,
                      nextColumn: column,
                      range: {
                        min: null,
                        max: column.tasks[0] || null,
                      },
                      task: { ...payload.task, completionDate: null },
                    });
              }}
            >
              {({ payload }, canDrop) => (
                <div className={styles.dropZone}>
                  {canDrop && (
                    <Task
                      task={payload.task}
                      project={project}
                      displayAllocationMenu={() => {}}
                      displayTagMenu={() => {}}
                    />
                  )}
                </div>
              )}
            </DropBox>
            <Filter
              data={tasks}
              filter={filter}
              extractCriteria={(task) => ({ task })}
            >
              {(filtered) => (
                <React.Fragment>
                  {filtered.length > 0 ? (
                    filtered.map((task, index) => (
                      <DropBox
                        key={task.uuid}
                        acceptedTypes={["MOVE_TASK"]}
                        priority={1}
                        isDisabled={(data) =>
                          isCompletedColumn &&
                          isConstrainedTask(data.payload?.task?.uuid)
                        }
                        drop={({ payload }) => {
                          payload.column.status.uuid !== column.status.uuid &&
                          column.status.percentComplete === 100
                            ? pushView({
                                key: UseCases.moveKanbanTask,
                                data: {
                                  currentColumn: payload.column,
                                  nextColumn: column,
                                  range: {
                                    min: task,
                                    max: column.tasks[index + 1] || null,
                                  },
                                  task: payload.task,
                                },
                              })
                            : moveKanbanTask({
                                currentColumn: payload.column,
                                nextColumn: column,
                                range: {
                                  min: task,
                                  max: column.tasks[index + 1] || null,
                                },
                                task: { ...payload.task, completionDate: null },
                              });
                        }}
                      >
                        {({ payload }, canDrop) => (
                          <React.Fragment>
                            <TaskCreationWrapper
                              rootList={rootList}
                              status={status}
                              column={column}
                              project={project}
                              range={{
                                min: task,
                                max: column.tasks[index + 1] || null,
                              }}
                            >
                              {!project.isTemplate ? (
                                <Draggable
                                  type="MOVE_TASK"
                                  className={styles.task}
                                  data={{ task, column }}
                                  draggedClassName={styles.dragged}
                                  isDisabled={isConstrainedTask(task.uuid)}
                                >
                                  <Task
                                    task={task}
                                    project={project}
                                    displayAllocationMenu={
                                      displayAllocationMenu
                                    }
                                    displayTagMenu={displayTagMenu}
                                  />
                                </Draggable>
                              ) : (
                                <Task
                                  task={task}
                                  project={project}
                                  displayAllocationMenu={displayAllocationMenu}
                                  displayTagMenu={displayTagMenu}
                                />
                              )}
                            </TaskCreationWrapper>
                            {canDrop && task.uuid !== payload.task.uuid && (
                              <div
                                className={classNames(
                                  styles.dropZone,
                                  styles.dropZoneInList,
                                )}
                              >
                                <div className={styles.task}>
                                  <Task
                                    task={payload.task}
                                    project={project}
                                    displayAllocationMenu={() => {}}
                                    displayTagMenu={() => {}}
                                  />
                                </div>
                              </div>
                            )}
                          </React.Fragment>
                        )}
                      </DropBox>
                    ))
                  ) : (
                    <TaskCreationWrapper
                      status={status}
                      rootList={rootList}
                      project={project}
                      column={column}
                      children={<React.Fragment />}
                      isSwitchDisabled
                    />
                  )}
                </React.Fragment>
              )}
            </Filter>
          </div>
        </div>
      )}
    </DropBox>
  );
}

export { Column };
